import { FC, useContext, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, Link } from "react-router-dom";
import { t } from "i18next";
import { App, Layout, Menu, Dropdown, MenuProps, Flex } from "antd";
import {
  LaptopOutlined,
  LoginOutlined,
  KeyOutlined,
  CheckCircleOutlined,
  MinusCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  PicLeftOutlined,
  SnippetsOutlined,
  FundOutlined,
  AuditOutlined,
  PayCircleOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import { userUpdatePassword } from "@/apis/user";
import { ROUTER_KEYS } from "@/router/routers";
import { SupplierPagesHomeWrap, SupplierSiderWrap } from "./style";
import UpdateSupplierPwd, {
  PwdFormField,
} from "./components/UpdateSupplierPwd";
import { supplierProfileLogout } from "@/pages/Profile/utils/logout";
import { ProfileItem } from "@/layout/HomeLayout/style";
import { getUserName } from "@/utils/user";
import { UserContext } from "@/layout/context/SettingsContext";
import Locale from "@/layout/HomeLayout/components/Locale";

const { Content } = Layout;

interface MenuInfo {
  key: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  children?: Array<MenuInfo>;
}

const SupplierPagesHome: FC = () => {
  const navigate = useNavigate();
  const { notification, modal } = App.useApp();
  const routerLocation = useLocation();
  const [[user]] = useContext(UserContext);
  const [isUpdatePwd, setIsUpdatePwd] = useState<boolean>(false);
  const [menuSelectKeys, setMenuSelectKeys] = useState(`${ROUTER_KEYS.SUPPLIER_REQUEST_HOME}?pageType=pending`);

  const menuItems: Array<MenuInfo> = [
    {
      key: "quotation",
      icon: <LaptopOutlined />,
      label: t("supplierLayout.quotation"),
      children: [
        {
          key: `${ROUTER_KEYS.SUPPLIER_REQUEST_HOME}?pageType=pending`,
          label: (
            <Link to={`${ROUTER_KEYS.SUPPLIER_REQUEST_HOME}?pageType=pending`}>
              <span>
                <ClockCircleOutlined /> {t("supplierLayout.pendingQuotation")}
              </span>
            </Link>
          ),
        },
        {
          key: `${ROUTER_KEYS.SUPPLIER_REQUEST_HOME}?pageType=done`,
          label: (
            <Link to={`${ROUTER_KEYS.SUPPLIER_REQUEST_HOME}?pageType=done`}>
              <span>
                <CheckCircleOutlined /> {t("supplierLayout.resolveQuotation")}
              </span>
            </Link>
          ),
        },
        {
          key: `${ROUTER_KEYS.SUPPLIER_REQUEST_HOME}?pageType=reject`,
          label: (
            <Link to={`${ROUTER_KEYS.SUPPLIER_REQUEST_HOME}?pageType=reject`}>
              <span>
                <MinusCircleOutlined /> {t("supplierLayout.other")}
              </span>
            </Link>
          ),
        },
      ],
    },
    {
      key: ROUTER_KEYS.SUPPLIER_PURCHASE_HOME,
      label: (
        <Link to={`${ROUTER_KEYS.SUPPLIER_PURCHASE_HOME}`}>
          <span>
            <ShoppingCartOutlined /> {t("supplierLayout.purchaseOrder")}
          </span>
        </Link>
      ),
    },
    {
      key: ROUTER_KEYS.SUPPLIER_DELIVERY_NOTICE,
      label: (
        <Link to={`${ROUTER_KEYS.SUPPLIER_DELIVERY_NOTICE}`}>
          <span>
            <PicLeftOutlined /> {t("supplierLayout.deliveryNotice")}
          </span>
        </Link>
      ),
    },
    {
      key: "business",
      icon: <SnippetsOutlined />,
      label: t("supplierLayout.business"),
      children: [
        {
          key: ROUTER_KEYS.SUPPLIER_BUSSINESS_RECONCILIATION,
          label: (
            <Link to={ROUTER_KEYS.SUPPLIER_BUSSINESS_RECONCILIATION}>
              <span>
                <FundOutlined /> {t("supplierLayout.reconciliation")}
              </span>
            </Link>
          ),
        },
        {
          key: ROUTER_KEYS.SUPPLIER_BUSSINESS_CONTTRACT,
          label: (
            <Link to={ROUTER_KEYS.SUPPLIER_BUSSINESS_CONTTRACT}>
              <span>
                <AuditOutlined /> {t("supplierLayout.contract")}
              </span>
            </Link>
          ),
        },
        {
          key: 'supplierQuotation',
          icon: <PayCircleOutlined />,
          label: t("supplierLayout.quotations"),
          children: [
            {
              key: `${ROUTER_KEYS.SUPPLIER_BUSSINESS_QUOTATION}?pageType=product`,
              label: (
                <Link to={`${ROUTER_KEYS.SUPPLIER_BUSSINESS_QUOTATION}?pageType=product`}>
                  <span>
                    <ShopOutlined /> {t("supplierLayout.proQuotations")}
                  </span>
                </Link>
              ),
            },
            {
              key: `${ROUTER_KEYS.SUPPLIER_BUSSINESS_QUOTATION}?pageType=my`,
              label: (
                <Link to={`${ROUTER_KEYS.SUPPLIER_BUSSINESS_QUOTATION}?pageType=my`}>
                  <span>
                    <UserOutlined /> {t("supplierLayout.myQuotations")}
                  </span>
                </Link>
              ),
            },
          ]
        },
      ],
    },
  ];

  const supplierItems: MenuProps["items"] = [
    {
      key: "pwd",
      label: (
        <ProfileItem>
          <KeyOutlined />
          <span className="txt">{t("supplierLayout.updatePwd")}</span>
        </ProfileItem>
      ),
      onClick: () => setIsUpdatePwd(true),
    },
    {
      key: "exit",
      label: (
        <ProfileItem>
          <LoginOutlined />
          <span className="txt">{t("supplierLayout.logOut")}</span>
        </ProfileItem>
      ),
      onClick: () => exitLoginHandle(),
    },
  ];

  useEffect(() => {
    const { pathname, search } = routerLocation;
    const decodedSearch = window.decodeURI(search);
    const currentKey = pathname + decodedSearch;

    const isChildKey = (menuItems: Array<MenuInfo>, currentKey: string): boolean => {
      const checkItems = (items: Array<MenuInfo>): boolean => {
        return items.some(item => {
          if (item.key === currentKey) return true;

          if (item.children) {
            return checkItems(item.children);
          }

          return false;
        });
      };

      return checkItems(menuItems) || pathname === ROUTER_KEYS.SUPPLIER_REQUEST_HOME;
    };
    
    const selectedKey = isChildKey(menuItems, currentKey)
      ? currentKey
      : menuItems[0]?.children?.[0]?.key || currentKey;

    setMenuSelectKeys(selectedKey);

    if (!isChildKey && pathname !== ROUTER_KEYS.SUPPLIER_REQUEST_HOME) {
      navigate(
        menuItems[0]?.children?.[0]?.key || ROUTER_KEYS.SUPPLIER_REQUEST_HOME,
        { replace: true }
      );
    }
  }, [routerLocation.pathname, routerLocation.search, menuItems, navigate]);

  const exitLoginHandle = () => {
    modal.confirm({
      title: t("layout.conLogout"),
      content: t("layout.conLogoutTip1"),
      onOk() {
        notification.success({ placement: 'top', message: t("layout.loggingOut"), duration: 1 });
        let timer = setTimeout(() => {
          supplierProfileLogout();
          clearTimeout(timer);
          navigate(ROUTER_KEYS.SUPPLIER_LOGIN, { replace: true });
        }, 200);
      },
    });
  };

  const submitPwd = (values: PwdFormField) => {
    userUpdatePassword(user?.id || "", values.password)
      .then(() => {
        notification.success({ placement: 'top',
          message: t("member.pwdChanged"),
          description: t("member.aginLogin"),
        });
        supplierProfileLogout();
        navigate(ROUTER_KEYS.SUPPLIER_LOGIN, { replace: true });
      })
      .catch((err) => {
        notification.error({ placement: 'top', message: t('supplierLayout.pwdError'), description: err });
      });
  };

  return (
    <SupplierPagesHomeWrap>
      <Layout>
        <SupplierSiderWrap width={200}>
          <Flex className="title--wrap" gap={10}>
            SUPPLIER
            <Locale />
          </Flex>
          <Menu
            mode="inline"
            style={{ height: "100%", borderRight: 0 }}
            items={menuItems}
            selectedKeys={[menuSelectKeys]}
          />
          <div className="supplier-opt-wrap">
            <Dropdown
              trigger={["click"]}
              menu={{ items: supplierItems }}
              placement="topRight"
            >
              <Flex gap={10} className="supplier-opt-drop">
                <UserOutlined />
                {!!user ? getUserName(user) : "SUPPLIER"}
              </Flex>
            </Dropdown>
          </div>
          {isUpdatePwd && (
            <UpdateSupplierPwd
              onCancel={() => setIsUpdatePwd(false)}
              onConfirm={(values) => submitPwd(values)}
            />
          )}
        </SupplierSiderWrap>
        <Layout style={{ padding: "0" }}>
          <Content
            style={{
              padding: 24,
              margin: 0,
              minHeight: 280,
            }}
          >
            <Outlet />
          </Content>
        </Layout>
      </Layout>
    </SupplierPagesHomeWrap>
  );
};

export default SupplierPagesHome;
